// @file Shared presentation store for Slideshow and Virtual Field Trip
//
// Manages page list, navigation state, and derived getters shared by both
// presentation modes.

import { isAppUsing } from '@@/bits/flip'
import { COVER_PAGE_INDEX, getCurrentPostCid } from '@@/bits/presentation'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { useSurfacePostPropertiesStore } from '@@/pinia/surface_post_properties'
import { useSurfaceSectionsStore } from '@@/pinia/surface_sections'
import { useSurfaceUserContributorsStore } from '@@/pinia/surface_user_contributors'
import { useSurfaceWishArrangementStore } from '@@/pinia/surface_wish_arrangement'
import type { Page } from '@@/types/presentation'
import { PresentationPageType } from '@@/types/presentation'
import { isEmpty } from 'es-toolkit/compat'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

const usePresentationStore = defineStore('presentation', () => {
  const surfaceSectionsStore = useSurfaceSectionsStore()
  const surfacePostsStore = useSurfacePostsStore()
  const surfaceWishArrangementStore = useSurfaceWishArrangementStore()
  const surfaceUserContributorsStore = useSurfaceUserContributorsStore()
  const surfacePostPropertiesStore = useSurfacePostPropertiesStore()

  //--------------------------------------------------------------------------
  // Data
  //--------------------------------------------------------------------------

  const hasLoadedPages = ref(false)

  // Drives the app-level loading spinner's lifetime: the same DOM element
  // stays mounted from page load through Cesium init until the intro fly-in
  // is about to start.
  const isCesiumReadyForFly = ref(false)
  function markCesiumReadyForFly(): void {
    isCesiumReadyForFly.value = true
  }

  const isDataLoaded = computed(() => {
    if (!surfacePostsStore.hasFetchedPosts) return false
    if (surfaceWishArrangementStore.isGroupBySection && !surfaceSectionsStore.hasFetchedSections) return false
    if (surfaceWishArrangementStore.isGroupByAuthor && !surfaceUserContributorsStore.areAuthorsFetched) return false
    if (
      surfaceWishArrangementStore.isGroupBySingleSelect &&
      !surfacePostPropertiesStore.hasFetchedInitialCustomPostProperties
    )
      return false

    return true
  })

  //--------------------------------------------------------------------------
  // Pages
  //--------------------------------------------------------------------------

  const currentPageIndex = ref<number | null>(null)

  const pages = computed(() => {
    if (!isDataLoaded.value) {
      return []
    }

    const populatedPages: Page[] = [
      {
        pageType: PresentationPageType.Cover,
      },
    ]

    const pushGroupPage = (groupId: number | string): void => {
      const pageType = isAppUsing('groupPostsV2') ? PresentationPageType.Group : PresentationPageType.Section

      populatedPages.push({
        pageType,
        entityId: groupId.toString(),
      })
    }

    const pushPostPage = (postCid: string): void => {
      // Only approved posts should be shown
      if (surfacePostsStore.postEntitiesByCid[postCid]?.published !== true) {
        return
      }

      populatedPages.push({
        pageType: PresentationPageType.Wish,
        entityId: postCid?.replace('c', ''),
      })
    }

    const hasPosts = !isEmpty(surfacePostsStore.sortedPostCids)
    const hasGroups = !surfaceWishArrangementStore.isGroupByNone && !isEmpty(surfaceWishArrangementStore.sortedGroups)

    if (hasGroups) {
      surfaceWishArrangementStore.sortedGroups.forEach((group) => {
        const allPostsInGroup = surfacePostsStore.allSortedPostsByGroup[group.id]

        const shouldShowGroup =
          !surfaceWishArrangementStore.isGroupByAuthor ||
          allPostsInGroup?.some((post) => surfacePostsStore.postEntitiesByCid[post.cid]?.published === true)

        if (shouldShowGroup) {
          pushGroupPage(group.id)
          allPostsInGroup?.forEach((post) => pushPostPage(post.cid))
        }
      })
    } else if (hasPosts) {
      surfacePostsStore.sortedPostCids.forEach(pushPostPage)
    }

    return populatedPages
  })

  //--------------------------------------------------------------------------
  // Derived navigation state
  //--------------------------------------------------------------------------

  const currentPageNumber = computed<number | null>(() =>
    currentPageIndex.value != null ? currentPageIndex.value + 1 : null,
  )

  const currentPage = computed<Page | null>(() => {
    if (currentPageIndex.value == null) return null
    return pages.value.length > currentPageIndex.value ? pages.value[currentPageIndex.value] : null
  })

  const currentPost = computed(() => {
    if (currentPage.value?.entityId == null) return null
    return surfacePostsStore.postEntitiesByCid[getCurrentPostCid(currentPage.value.entityId)]
  })

  const currentGroup = computed(() => {
    if (currentPage.value?.entityId == null) return null
    if (
      currentPage.value.pageType !== PresentationPageType.Section &&
      currentPage.value.pageType !== PresentationPageType.Group
    )
      return null

    return (
      surfaceWishArrangementStore.sortedGroups.find((group) => group.id?.toString() === currentPage.value?.entityId) ??
      null
    )
  })

  const numberOfPages = computed(() => pages.value.length)
  const lastPageIndex = computed(() => numberOfPages.value - 1)

  const prevPageIndex = computed<number | null>(() =>
    currentPageIndex.value != null && currentPageIndex.value > 0 ? currentPageIndex.value - 1 : null,
  )

  const nextPageIndex = computed<number | null>(() =>
    currentPageIndex.value != null && currentPageIndex.value < lastPageIndex.value ? currentPageIndex.value + 1 : null,
  )

  const isFirstPage = computed(() => currentPageIndex.value === COVER_PAGE_INDEX)
  const isLastPage = computed(() => currentPageIndex.value === numberOfPages.value - 1)

  //--------------------------------------------------------------------------
  // Panel state (Field Trip share panels)
  //--------------------------------------------------------------------------

  const isSharePanelOpen = ref(false)
  const isShareViaAnotherAppPanelOpen = ref(false)
  const isEmbedPanelOpen = ref(false)

  return {
    // Data
    hasLoadedPages,
    isCesiumReadyForFly,
    markCesiumReadyForFly,
    isDataLoaded,

    // Pages
    currentPageIndex,
    pages,

    // Derived
    currentPageNumber,
    currentPage,
    currentPost,
    currentGroup,
    numberOfPages,
    lastPageIndex,
    prevPageIndex,
    nextPageIndex,
    isFirstPage,
    isLastPage,

    // Panel state
    isSharePanelOpen,
    isShareViaAnotherAppPanelOpen,
    isEmbedPanelOpen,
  }
})
export { usePresentationStore }
